import { Component, Input, OnDestroy, OnInit, inject, viewChild, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, ParamMap, Router, RouterLink } from "@angular/router";
import { CardComponent } from "common/card/card.component";
import { MutexService } from "common/concurrency/mutex.service";
import { DataViewComponent, Mode } from "common/data/data-view.component";
import { PageableDataSource } from "common/data/pageable-data-source.logic";
import { DialogService } from "common/dialog/dialog.service";
import { ProcessElementsResource, UserGroupResource } from "core/stages-client";
import { ViewService } from "core/view.service";
import { UserGroupsStore } from "process/usergroups/user-groups-store.logic";
import { BaseComponent } from "process/view/base.component";
import { ComponentService } from "process/view/component.service";
import { lastValueFrom, Observable, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";
import { AsyncPipe } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MenuComponent } from "common/menu/menu.component";
import { ResponsiveDataViewComponent } from "common/data/responsive-data-view.component";

@Component({
	selector: "stages-user-groups",
	templateUrl: "./user-groups.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [TranslateModule, RouterLink, MenuComponent, ResponsiveDataViewComponent, AsyncPipe],
})
export class UserGroupsComponent extends BaseComponent implements OnInit, OnDestroy {
	private viewService = inject(ViewService);
	private mutexService = inject(MutexService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private card = inject(CardComponent);
	private processElementsResource = inject(ProcessElementsResource);
	private dialogService = inject(DialogService);
	private userGroupResource = inject(UserGroupResource);

	UserGroupsComponent = UserGroupsComponent;
	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	pageSize: number = 10;

	// TODO: Skipped for migration because:
	//  Your application code writes to the input. This prevents migration.
	@Input()
	translateNone?: string;

	readonly dataView =
		viewChild.required<DataViewComponent<stages.administration.usergroup.role.UserGroupAssignment>>("dataView");

	params$!: Observable<ParamMap>;
	dataSource!: PageableDataSource<stages.administration.usergroup.role.UserGroupAssignment>;
	menuItems: MenuItem[] = [];
	userGroupContainer$!: Observable<stages.process.ViewableUserGroupContainer | undefined>;
	self$!: Observable<stages.process.ProcessView>;
	private subscription!: Subscription;

	constructor() {
		const componentService = inject(ComponentService);

		super(componentService);
	}

	ngOnInit(): void {
		this.self$ = this.viewService.awaitSelfElementObservable();
		this.dataSource = new PageableDataSource(
			new UserGroupsStore(
				lastValueFrom(this.self$.pipe(take(1))),
				this.processElementsResource,
				this.userGroupResource,
				this.pageSize,
			),
			this.mutexService,
			this.router,
			this.route,
		);

		this.params$ = this.route.paramMap;
		this.userGroupContainer$ = this.self$.pipe(map((self) => self.userGroupContainer));

		// prevent ExpressionChangedAfterItHasBeenCheckedError
		setTimeout(() => {
			this.subscription = this.self$.subscribe((view) => {
				this.card.menuItems = [
					{
						name: "add",
						iconClass: "ico ico-add",
						disabled: () => {
							return (
								!view.userGroupContainer ||
								!view.userGroupContainer.allowedOperations.AssignRoles ||
								!this.isValidVersion(view)
							);
						},
						on: () => {
							this.dataView().enterAdd(false);
						},
					},
					{
						name: "delete",
						iconClass: "ico ico-delete",
						disabled: () => {
							return (
								!view.userGroupContainer ||
								!view.userGroupContainer.allowedOperations.UnassignRoles ||
								!this.isValidVersion(view)
							);
						},
						on: () => {
							this.dataView().enterDelete();
						},
					},
				];

				this.menuItems = [
					{
						name: "delete",
						iconClass: "ico ico-delete",
						disabled: (userGroup: stages.administration.usergroup.role.UserGroupAssignment) => {
							return !userGroup.allowedOperations.UnassignRoles || !this.isValidVersion(view);
						},
						on: async (userGroupAssignment: stages.administration.usergroup.role.UserGroupAssignment) => {
							if (
								await this.dialogService.confirm(
									"process.element.usergroupassignments.unassign.confirm",
									{ name: userGroupAssignment.name },
									"delete",
									"cancel",
									true,
								)
							) {
								this.dataView().dataSource.delete(
									[userGroupAssignment.id!],
									"unassignUserGroup" + userGroupAssignment.id!,
								);
							}
						},
					},
				];
			});

			const dataView = this.dataView();
			if (dataView) {
				dataView.buttons = [
					{
						class: "button sm cancel",
						translate: "cancel",
						click: () => {
							this.dataView().mode = Mode.VIEW;
						},
						visible: () => {
							return this.dataView().mode === Mode.DELETE;
						},
						disabled: () => {
							return false;
						},
					},
					{
						class: "button sm delete",
						translate: "delete",
						click: () => {
							void this.dataView().dataSource.delete(this.dataView().selection.selected, "userGroupAssignments");
							this.dataView().mode = Mode.VIEW;
						},
						visible: () => {
							return this.dataView().mode === Mode.DELETE;
						},
						disabled: () => {
							return this.dataView().selection.isEmpty();
						},
					},
				];
			}
		});
	}

	isValidVersion(view: stages.process.ProcessView): boolean {
		return view.process && view.process.isValidVersion;
	}

	ngDestroy(): void {
		if (this.dataSource) {
			this.dataSource.disconnect();
		}

		if (this.subscription) {
			this.subscription.unsubscribe();
		}
	}

	static getId(userGroupAssignment: stages.administration.usergroup.role.UserGroupAssignment): string {
		return userGroupAssignment.id!;
	}

	static isDisabled(): boolean {
		return false;
	}
}
